import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EMPTY, Subscription, catchError } from 'rxjs';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportStreamService } from '../core/support-stream.service';
import { SupportApiService } from '../core/support-api.service';
import { TicketMessageDetail } from '../core/models';

/**
 * Component 1 of the agent console (ruling §14) — a live conversation panel,
 * explicitly NOT a `<bt-sidekick>` instance. It is a thin SSE subscription
 * that triggers a thread refetch on any frame; message bodies are always
 * resolved via SupportApiService.getTicketMessages (ruling §12 — the frame
 * itself carries no body text, so a frame is only ever a "go refetch" signal,
 * never a source of truth).
 *
 * `sourceConversationId` is the customer's in-product chat context reference
 * (ruling §8): it may be null, or may point at a per-org conversation that
 * has since been purged. This panel renders the reference (or its absence)
 * explicitly rather than assuming it still resolves.
 *
 * Failure domain: a fetch failure here degrades only this panel (spec's
 * "separate failure domain" requirement) — it never raises the app-wide
 * DegradedModeService banner, since reply/escalate/assign must stay usable
 * even if the live-conversation refetch is failing.
 */
@Component({
  selector: 'app-live-panel',
  standalone: true,
  imports: [CommonModule, NgbTooltip],
  template: `
    <div class="live-panel" data-testid="live-panel">
      <div class="live-panel__head" ngbTooltip="The customer's in-product live chat, mirrored here in real time. Message bodies are always re-fetched through the authorized read path.">
        <span class="live-dot"></span> Live conversation
      </div>
      <div class="live-panel__body">
        @if (sourceConversationId) {
          <p class="live-meta" data-testid="linked-conversation-ref"
             ngbTooltip="A reference to the customer's in-product Sidekick chat, resolved at read time — it may no longer be available if the per-org conversation store has since purged it">
            Linked conversation: <span class="mono">{{ sourceConversationId }}</span>
          </p>
        } @else {
          <p class="live-meta" data-testid="no-linked-conversation">No linked in-product conversation for this ticket.</p>
        }
        @if (fetchError) {
          <p data-testid="live-panel-fetch-error" class="live-panel-error"
             ngbTooltip="This panel could not refresh the conversation thread. Reply, escalate, and assign are unaffected.">
            Live conversation is temporarily unavailable.
          </p>
        }
        @for (message of messages; track message.id) {
          <p class="live-msg" [attr.data-testid]="'live-message-' + message.id">
            <span class="live-msg__who">{{ message.author_type }}</span> {{ message.body }}
          </p>
        } @empty {
          @if (!fetchError) { <p class="live-meta live-meta--muted">Waiting for live activity…</p> }
        }
      </div>
    </div>
  `,
  styles: [`
    .live-panel__head {
      display: flex; align-items: center; gap: 0.45rem; padding: 0.75rem 1rem;
      font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 700; text-transform: uppercase;
      letter-spacing: 0.05em; color: var(--bt-text-secondary); border-bottom: 1px solid var(--bt-border-light);
    }
    .live-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--bt-status-success); box-shadow: 0 0 0 3px color-mix(in srgb, var(--bt-status-success) 25%, transparent); }
    .live-panel__body { padding: 0.85rem 1rem; display: flex; flex-direction: column; gap: 0.4rem; }
    .live-meta { margin: 0; font-size: 13px; color: var(--bt-text-secondary); }
    .live-meta--muted { color: var(--bt-text-muted); font-style: italic; }
    .mono { font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 12px; }
    .live-msg { margin: 0; font-size: 13px; color: var(--bt-text-primary); }
    .live-msg__who { font-weight: 700; color: var(--bt-text-muted); text-transform: capitalize; margin-right: 0.25rem; }
    .live-panel-error { margin: 0; color: var(--bt-status-warning); font-weight: 600; font-size: 13px; }
  `],
})
export class LivePanelComponent implements OnInit, OnDestroy {
  private stream = inject(SupportStreamService);
  private api = inject(SupportApiService);
  private subscription?: Subscription;

  @Input() ticketId = '';
  @Input() sourceConversationId: string | null = null;
  messages: TicketMessageDetail[] = [];
  fetchError = false;

  ngOnInit(): void {
    this.refetchThread();
    this.subscription = this.stream.connect(this.ticketId).subscribe(() => {
      // Ruling §12 — the frame carries no body text; any frame is just a
      // signal to refetch via the authorized read path.
      this.refetchThread();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private refetchThread(): void {
    this.api.getTicketMessages(this.ticketId).pipe(
      catchError(() => {
        // Isolated failure domain: only this panel shows the failure state.
        // Never delegate to DegradedModeService — that banner is app-wide and
        // would incorrectly imply reply/escalate/assign are also affected.
        // EMPTY completes without emitting, so the success handler below
        // never runs and can't clobber fetchError back to false.
        this.fetchError = true;
        return EMPTY;
      }),
    ).subscribe((messages) => {
      this.fetchError = false;
      this.messages = messages;
    });
  }
}
